
import React, { useState, useRef } from 'react';
import { MealSession, Language, MealTypeKey, FoodItem, VisualDensity, CustomRecipe } from '../types';
import { t } from '../locales';
import SessionEditModal from './SessionEditModal';
import MealTypeModal from './MealTypeModal';
import ManualEntryModal from './ManualEntryModal';
import AddMethodModal from './AddMethodModal';
import { QuantityEditModal } from './QuantityEditModal';
import { ParsedItem } from '../services/textParsingService';

interface FoodListProps {
  session: MealSession;
  onAddMore: (sessionId: string) => void;
  onScan: (sessionId: string) => void; // New
  onCamera: (sessionId: string) => void; // New
  onDeleteItem: (itemId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onUpdateSessionTime: (sessionId: string, newDate: Date) => void;
  onUpdateItem: (itemId: string, quantity: number, unit: string) => void;
  onUpdateRecipe?: (itemId: string, updatedItem: FoodItem, saveAsCustom: boolean) => void; 
  onUpdateSessionType: (type: MealTypeKey) => void;
  onManualAdd: (items: ParsedItem[]) => Promise<void>;
  onToggleStar: (sessionId: string) => void;
  isRecordingThisSession: boolean;
  language: Language;
  isFirstSession?: boolean;
  isOpen: boolean; 
  onToggle: () => void;
  density?: VisualDensity;
  customRecipes?: CustomRecipe[]; 
  onMerge?: (sourceId: string) => void; 
}

const FoodList: React.FC<FoodListProps> = ({ 
    session, 
    onAddMore, 
    onScan,
    onCamera,
    onDeleteItem, 
    onDeleteSession, 
    onUpdateSessionTime,
    onUpdateItem,
    onUpdateRecipe,
    onUpdateSessionType,
    onManualAdd,
    onToggleStar,
    isRecordingThisSession, 
    language,
    isFirstSession = false,
    isOpen,
    onToggle,
    density = 'standard',
    customRecipes = [],
    onMerge
}) => {
  const [isEditTimeOpen, setIsEditTimeOpen] = useState(false);
  const [isMealTypeOpen, setIsMealTypeOpen] = useState(false);
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  const [isAddMethodOpen, setIsAddMethodOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const [expandedRecipeIds, setExpandedRecipeIds] = useState<Set<string>>(new Set());

  const toggleRecipe = (id: string) => {
      const newSet = new Set(expandedRecipeIds);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      setExpandedRecipeIds(newSet);
  };

  const truncate = (str: string, len: number = 16) => str.length > len ? str.substring(0, len - 1) + '...' : str;

  const containerRounded = density === 'compact' ? 'rounded-md' : density === 'comfort' ? 'rounded-xl' : 'rounded-lg';
  const headerPadding = density === 'compact' ? 'px-3 py-2' : density === 'comfort' ? 'px-6 py-4' : 'px-4 py-3';
  const headerIconSize = density === 'compact' ? 'p-1.5' : 'p-2';
  const headerTextSize = density === 'compact' ? 'text-xs' : 'text-sm font-medium';
  const rowPadding = density === 'compact' ? 'py-1' : density === 'comfort' ? 'py-4' : 'py-2';
  const itemTextSize = density === 'compact' ? 'text-xs' : 'text-sm';
  const cellPaddingLeft = density === 'compact' ? 'pl-2' : 'pl-3';
  
  const lastTapRef = useRef<number>(0);
  const handleContainerClick = () => {
      const now = Date.now();
      if (now - lastTapRef.current < 300) onToggleStar(session.id);
      else onToggle();
      lastTapRef.current = now;
  };

  const getMealType = (date: Date): MealTypeKey => {
    if (session.manualMealType) return session.manualMealType;
    const hours = date.getHours();
    if (hours >= 4 && hours < 11) return 'breakfast';
    if (hours >= 11 && hours < 15) return 'lunch';
    if (hours >= 15 && hours < 18) return 'afternoonSnack';
    if (hours >= 18 || hours < 4) return 'dinner';
    return 'snack';
  };

  const getMealIcon = () => {
    const type = getMealType(session.timestamp);
    const iconClass = density === 'compact' ? "h-4 w-4" : "h-5 w-5";
    switch (type) {
        case 'breakfast': return <svg xmlns="http://www.w3.org/2000/svg" className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>;
        case 'lunch': return <svg xmlns="http://www.w3.org/2000/svg" className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
        case 'dinner': return <svg xmlns="http://www.w3.org/2000/svg" className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>;
        default: return <svg xmlns="http://www.w3.org/2000/svg" className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" /></svg>;
    }
  };

  const getContextLabel = () => {
    const mealLabel = t(language, getMealType(session.timestamp));
    const locale = language === 'fr' ? 'fr-FR' : 'en-US';
    return `${mealLabel} • ${session.timestamp.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`;
  };

  const containerClasses = session.isStarred 
    ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700/50 ring-1 ring-yellow-100 dark:ring-yellow-900/30"
    : `bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 ${isRecordingThisSession ? 'border-red-400 ring-1 ring-red-100' : ''}`;

  return (
    <>
    <div draggable={!isRecordingThisSession} onDragStart={(e) => e.dataTransfer.setData('text/plain', session.id)} onDragOver={(e) => { e.preventDefault(); if (!isDragOver) setIsDragOver(true); }} onDragLeave={() => setIsDragOver(false)} onDrop={(e) => { e.preventDefault(); setIsDragOver(false); const sid = e.dataTransfer.getData('text/plain'); if (sid && sid !== session.id && onMerge) onMerge(sid); }} className={`shadow-sm overflow-hidden border transition-all mb-4 ${containerRounded} ${containerClasses} ${isDragOver ? 'border-dashed border-emerald-500 ring-2 ring-emerald-500/20 scale-[1.02] bg-emerald-50/50' : ''}`}>
      {isDragOver && <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"><div className="bg-emerald-600 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg animate-pulse uppercase tracking-widest">{language === 'fr' ? 'Relâcher pour fusionner' : 'Drop to Merge'}</div></div>}
      <div onClick={handleContainerClick} className={`${headerPadding} flex items-center justify-between cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors select-none group relative`}>
        {session.isStarred && <div className="absolute top-2 right-2 text-yellow-400"><svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg></div>}
        <div className="flex items-center gap-3">
           <button onClick={(e) => { e.stopPropagation(); setIsMealTypeOpen(true); }} className={`${headerIconSize} rounded-full transition-colors hover:bg-gray-200 dark:hover:bg-gray-700 ${isRecordingThisSession ? 'bg-red-100 text-red-600' : session.isStarred ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/50 dark:text-yellow-400' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'}`}>{isRecordingThisSession ? <div className="flex items-center gap-1 px-1"><div className="w-1 h-3 bg-red-500 animate-pulse"></div><div className="w-1 h-4 bg-red-500 animate-pulse delay-75"></div><div className="w-1 h-2 bg-red-500 animate-pulse delay-150"></div></div> : getMealIcon()}</button>
           <div><h3 className={`${headerTextSize} text-gray-800 dark:text-gray-200`}>{getContextLabel()}</h3><p className="text-[10px] text-gray-500 dark:text-gray-400">{session.items.length} {t(language, 'itemsLoggedCount')}</p></div>
        </div>
        <div className="flex items-center gap-2">
            <button onClick={(e) => { e.stopPropagation(); setIsEditTimeOpen(true); }} className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 dark:text-gray-500 transition-colors"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg></button>
            <button onClick={(e) => { e.stopPropagation(); setIsAddMethodOpen(true); }} disabled={isRecordingThisSession} className={`p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition-colors ${isRecordingThisSession ? 'opacity-0' : 'opacity-100'}`}><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg></button>
            <button onClick={(e) => { e.stopPropagation(); onDeleteSession(session.id); }} className="p-1.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-300 hover:text-red-500 transition-colors ml-1"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
        </div>
      </div>
      {isOpen && !session.isStarred && <div className="bg-[#3F88C5]/10 px-6 py-1 text-[9px] text-[#3F88C5] text-center font-bold tracking-tight">{t(language, 'doubleTapHint')}</div>}
      {isOpen && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {session.items.length === 0 ? <tr><td colSpan={3} className="px-6 py-6 text-center text-xs text-gray-400 italic">{t(language, 'listeningItems')}</td></tr> : (
                session.items.map((item) => {
                    const isClickable = item.isComposite || (item.ingredients && item.ingredients.length > 0);
                    const isExpanded = expandedRecipeIds.has(item.id);
                    
                    const matchedRecipe = customRecipes.find(r => r.name.toLowerCase() === item.name.toLowerCase());
                    
                    const isCookbook = matchedRecipe?.source === 'home';
                    const isHomeRecipe = matchedRecipe?.source === 'adapted' || ['home', 'maison', 'my', 'homemade'].some(kw => item.name.toLowerCase().includes(kw));
                    
                    const isGenericRecipe = isClickable && !matchedRecipe && !item.isPantry;

                    const displayUnit = item.unit || t(language, 'u_piece');

                    return (
                        <React.Fragment key={item.id}>
                        <tr onClick={() => { if (isClickable) toggleRecipe(item.id); }} className={`${isClickable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : 'cursor-default'} transition-colors ${isExpanded ? 'bg-gray-50 dark:bg-gray-700/50' : ''}`}>
                            <td className={`${cellPaddingLeft} ${rowPadding} w-full overflow-hidden`}>
                                <div className="flex items-center gap-1.5 w-full">
                                    {isClickable && <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 text-gray-400 transition-transform flex-shrink-0 -ml-1 ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>}
                                    <div className={`truncate ${itemTextSize} font-medium ${item.isPantry ? 'text-orange-700 dark:text-orange-400' : (isHomeRecipe || isCookbook) ? 'text-[#3F88C5]' : 'text-gray-900 dark:text-gray-200'} capitalize flex-1`} title={item.name}>{item.name}</div>
                                </div>
                            </td>
                            <td className={`px-1 ${rowPadding} text-right whitespace-nowrap w-auto`}>
                                <div className="flex justify-end items-center gap-1.5">
                                    <button 
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setEditingItemId(item.id); }}
                                        className={`px-1 py-0.5 rounded text-[10px] font-bold transition-all border shrink-0 flex items-center gap-1 shadow-sm ${
                                            item.isPantry ? 'bg-orange-50 border-orange-200 text-orange-700' :
                                            (isHomeRecipe || isCookbook) ? 'bg-blue-50 border-blue-200 text-[#3F88C5]' :
                                            'bg-emerald-50 border-emerald-100 text-emerald-700'
                                        }`}
                                    >
                                        <span>{item.quantity || 1}</span>
                                        <span className="opacity-60 font-medium lowercase truncate max-w-[35px]">{displayUnit}</span>
                                    </button>

                                    {item.isPantry && <span className="bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300 text-[8px] px-1.5 py-0.5 rounded font-black shrink-0 shadow-sm border border-orange-200/50 hidden sm:inline-block">PANTRY</span>}
                                    {isGenericRecipe && <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-[8px] px-1.5 py-0.5 rounded font-black shrink-0 shadow-sm border border-gray-200/50 uppercase hidden sm:inline-block">{t(language, 'recipeLabel')}</span>}
                                    {isCookbook && <span className="bg-[#3F88C5]/10 text-[#3F88C5] text-[8px] px-1.5 py-0.5 rounded font-black shrink-0 shadow-sm border border-[#3F88C5]/20 uppercase hidden sm:inline-block">{t(language, 'cookbookLabel')}</span>}
                                    {isHomeRecipe && <span className="bg-[#3F88C5]/10 text-[#3F88C5] text-[8px] px-1.5 py-0.5 rounded font-black shrink-0 shadow-sm border border-[#3F88C5]/20 uppercase hidden sm:inline-block">{t(language, 'homeRecipeLabel')}</span>}
                                </div>
                            </td>
                            <td className={`pr-4 pl-2 ${rowPadding} text-right w-10 whitespace-nowrap`}>
                                <button type="button" onClick={(e) => { e.stopPropagation(); onDeleteItem(item.id); }} className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                            </td>
                        </tr>
                        {isExpanded && item.ingredients && item.ingredients.length > 0 && (
                            <tr className="bg-gray-50/50 dark:bg-gray-700/20">
                                <td colSpan={3} className="px-0 py-0 border-t border-gray-100 dark:border-gray-700/30">
                                    <div className="py-2 pl-7 pr-4 flex flex-wrap gap-x-2 gap-y-1">
                                        {item.ingredients.map((ing) => (
                                            <div key={ing.id} className="flex items-center gap-1 text-[9px] text-gray-500 dark:text-gray-400 bg-black/5 dark:bg-white/5 px-1.5 py-0.5 rounded-md border border-gray-100/50 dark:border-gray-700/50">
                                                <div className={`w-0.5 h-0.5 rounded-full shrink-0 ${item.isPantry ? 'bg-orange-400' : (isHomeRecipe || isCookbook) ? 'bg-[#3F88C5]/50' : session.isStarred ? 'bg-yellow-400' : 'bg-emerald-400'}`}></div>
                                                <span className="capitalize whitespace-nowrap" title={ing.name}>{truncate(ing.name)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                            </tr>
                        )}
                        </React.Fragment>
                    );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
    {isEditTimeOpen && <SessionEditModal isOpen={isEditTimeOpen} onClose={() => setIsEditTimeOpen(false)} currentDate={session.timestamp} onSave={(newDate) => onUpdateSessionTime(session.id, newDate)} language={language} />}
    {isMealTypeOpen && <MealTypeModal isOpen={isMealTypeOpen} onClose={() => setIsMealTypeOpen(false)} currentType={getMealType(session.timestamp)} onSelect={onUpdateSessionType} language={language} />}
    {isManualEntryOpen && <ManualEntryModal isOpen={isManualEntryOpen} onClose={() => setIsManualEntryOpen(false)} type="food" onSave={onManualAdd} language={language} customRecipes={customRecipes} />}
    {isAddMethodOpen && (
        <AddMethodModal 
            isOpen={isAddMethodOpen} 
            onClose={() => setIsAddMethodOpen(false)} 
            onSelect={(m) => { 
                setIsAddMethodOpen(false); 
                if(m === 'audio') onAddMore(session.id); 
                else if(m === 'text') setIsManualEntryOpen(true); 
                else if(m === 'barcode') onScan(session.id); 
                else if(m === 'camera') onCamera(session.id);
            }} 
            language={language} 
            type="meal" 
        />
    )}
    {editingItemId && (
        <QuantityEditModal 
            isOpen={!!editingItemId}
            onClose={() => setEditingItemId(null)}
            currentItem={{ ...session.items.find(i => i.id === editingItemId), unit: session.items.find(i => i.id === editingItemId)?.unit || t(language, 'u_piece') }}
            onSave={(q, u) => { onUpdateItem(editingItemId, q, u); setEditingItemId(null); }}
            language={language}
            theme={(customRecipes.some(r => r.name.toLowerCase() === session.items.find(i => i.id === editingItemId)?.name.toLowerCase())) ? 'yellow' : 'emerald'}
        />
    )}
    </>
  );
};

export default FoodList;
